<template>
  <div class="step-container client">
    <help-drawer :title="helpTitle">
      <div>
        <p>
          Your marital status helps us understand your account setup and 
          beneficiary requirements. This information is required for 
          regulatory compliance.
        </p>
      </div>
    </help-drawer>
    <v-form name="PershingMaritalStatus" class="form" data-test-id="pershing-marital-status-form">
      <v-container class="client-step-container">
        <syncing-indicator :pageName="MENUS.CLIENT.PERSHING_MARITAL_STATUS.id"></syncing-indicator>
        <v-row class="mb-8">
          <v-col class="instructions">
            <h1 data-test-id="pershing-marital-status-header">
              <span class="headerlight">
                What is your <strong>marital status?</strong>
              </span>
            </h1>
            <div class="help-link">
              <a @click="toggleHelp()" data-test-id="help-link">{{ helpTitle }}</a>
            </div>
          </v-col>
        </v-row>
        
        <!-- Show syncing message when syncing -->
        <div v-if="isSyncing" class="syncing-message" data-test-id="syncing-message">
          <p>Saving your marital status...</p>
        </div>
        
        <!-- Show data submitted message only when completed, not syncing, and form hidden -->
        <data-submitted-message
          v-if="isPershingMaritalStatusComplete && formHidden && !isSyncing"
          :submitted="isPershingMaritalStatusComplete"
          v-on:hide-form="toggleFormState"
          data-test-id="data-submitted-message"
        />
        
        <!-- Hide form content when syncing or when formHidden is true -->
        <div v-if="!formHidden && !isSyncing">
          <v-row class="justify-center">
            <v-col class="step-form-container">
              <label for="maritalStatus" class="required" data-test-id="marital-status-label">
                Marital Status
              </label>
              <v-select
                v-model="maritalStatus"
                :items="maritalStatusOptions"
                :backgroundColor="inputBackgroundColor"
                outlined
                dense
                hide-details="auto"
                placeholder="Choose Marital Status"
                @change="$v.maritalStatus.$touch()"
                :error-messages="maritalStatusErrors"
                data-test-id="marital-status-select"
                class="mb-4"
              />
            </v-col>
          </v-row>

          <div class="skip-for-now">
            <a @click="skip()" data-test-id="skip-button">Skip for now</a>
          </div>
        </div>
      </v-container>
    </v-form>
    <bottom-nav v-on:go-prev="prev()" v-on:go-next="next()" data-test-id="pershing-marital-status-nav"></bottom-nav>
  </div>
</template>

<script>
import HelpDrawer from '@/components/HelpDrawerComponent';
import BottomNav from '@/components/common/BottomNav';
import DataSubmittedMessage from '@/components/client/DataSubmittedMessage';
import InterviewClientMixin from '@/common/mixins/interview.client.mixin';
import { required } from 'vuelidate/lib/validators';
import { MENUS } from '@/common/constants';
import SyncingIndicator from '@/components/common/SyncingIndicator.vue';

export default {
  name: 'PershingMaritalStatus',

  components: { BottomNav, HelpDrawer, DataSubmittedMessage, SyncingIndicator },
  mixins: [InterviewClientMixin],

  data: () => ({
    maritalStatus: null,
    MENUS: MENUS,
    maritalStatusOptions: [
      { text: 'Single', value: 'Single' },
      { text: 'Married', value: 'Married' },
      { text: 'Divorced', value: 'Divorced' },
      { text: 'Domestic Partner', value: 'Domestic Partner' },
      { text: 'Widowed', value: 'Widowed' },
    ],
  }),

  validations() {
    return {
      maritalStatus: {
        required,
      },
      validationGroup: ['maritalStatus'],
    };
  },

  computed: {
    // Is pershing marital status complete
    isPershingMaritalStatusComplete() {
      return this.isPageComplete(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id);
    },
    
    // Check if the page is currently syncing
    isSyncing() {
      const page = this.getPage(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id);
      return page && page.status === 'IN_PROGRESS';
    },

    /* Dynamic errors */
    maritalStatusErrors() {
      const errors = [];
      if (!this.$v.maritalStatus.$dirty) return errors;
      !this.$v.maritalStatus.required &&
        errors.push('A selection is required');
      return errors;
    },

    helpTitle() {
      return 'Why do you need this?';
    },
  },

  methods: {
    /* Nav to Next Page in flow */
    next(route = null) {
      let to = route || this.pages[this.pageIndex + 1];

      if (!this.formHidden) {
        this.$v.$touch();
        if (this.$v.$invalid) return;
      } else {
        this.goNext(to);
        return;
      }

      const data = {
        maritalStatus: this.maritalStatus,
      };

      // Mark page as syncing immediately for better user feedback
      this.markPageAsSyncing(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id);

      this.completeStep(MENUS.CLIENT.PERSHING_MARITAL_STATUS.id, data)
        .then(() => {
          // Set formHidden to true after successful completion
          this.formHidden = true;
          this.goNext(to);
        })
        .catch(() => {});
    },

    /* Nav to Previous Page in flow */
    prev() {
      this.goPrev(this.pages[this.pageIndex - 1]);
    },

    /* Skip to next Page in flow */
    skip() {
      // Fetch updated interview progress before navigation
      this.$store.dispatch('GET_INTERVIEW_PROGRESS').finally(() => {
        this.goNext(this.pages[this.pageIndex + 1]);
      });
    },
    
    // Toggle form state with validation reset
    toggleFormState(hide) {
      this.formHidden = hide;
      
      // Reset form validation
      this.$nextTick(() => {
        if (this.$v) {
          this.$v.$reset();
        }
      });
    },
    
    // Update formHidden state when sync status changes
    handleSyncStatusChange() {
      // If completed, hide the form
      if (this.isPershingMaritalStatusComplete) {
        this.formHidden = true;
      }
    }
  },
  
  watch: {
    // Watch for changes in the sync status
    isSyncing(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        // Sync has completed, check if form should be hidden
        this.$nextTick(() => {
          this.handleSyncStatusChange();
        });
      }
    },
    // Watch for changes in the completion status
    isPershingMaritalStatusComplete(newVal) {
      if (newVal === true) {
        this.formHidden = true;
      }
    }
  },
  
  created() {
    this.pages = this.interviewPages.getInterviewPages();
    this.pageIndex = this.pages.findIndex(page => {
      if (typeof page === 'string') {
        return page === MENUS.CLIENT.PERSHING_MARITAL_STATUS.id;
      } else if (typeof page === 'object' && page.name) {
        return page.name === MENUS.CLIENT.PERSHING_MARITAL_STATUS.id;
      }
      return false;
    });
    
    // Initialize form data for current interview
    this.formHidden = this.isPershingMaritalStatusComplete;
  },
  
  mounted() {
    // Check form state on mount
    this.$nextTick(() => {
      // If pershing marital status is complete, hide the form
      if (this.isPershingMaritalStatusComplete) {
        this.formHidden = true;
      }
    });
    
    // Start polling for updates to keep synced state updated
    this.$store.dispatch('GET_INTERVIEW_PROGRESS');
  }
};
</script>

<style lang="scss" scoped>
.syncing-message {
  text-align: center;
  margin: 20px 0;
  
  p {
    color: #666;
    font-style: italic;
  }
}

.help-link {
  padding-top: 8px;
  
  a {
    color: #1976d2;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.skip-for-now {
  margin-top: 20px;
  text-align: center;
  
  a {
    color: #666;
    text-decoration: underline;
    cursor: pointer;
    
    &:hover {
      text-decoration: none;
    }
  }
}

.step-form-container {
  max-width: 600px;
  margin: 0 auto;
}
</style>